import { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useSEO } from '../../utils/seo';
import Navbar from '../home/components/Navbar';
import BlogFooter from './components/BlogFooter';
import { blogPosts } from '../../mocks/blog';
import WhatsAppChat from '../../components/feature/WhatsAppChat';

// Different content for each blog post
const blogContents: Record<number, any> = {
  1: {
    intro: "The world of interior design is constantly evolving, and 2024 brings exciting new trends that blend functionality with aesthetic appeal. From sustainable materials to smart home integration, this year's trends reflect our changing lifestyles and values.",
    sections: [
      {
        title: "Biophilic Design Takes Center Stage",
        content: [
          "Bringing nature indoors has become more than just a trend—it's a lifestyle choice. Biophilic design incorporates natural elements like plants, natural light, and organic materials to create spaces that promote well-being and reduce stress.",
          "Large windows, living walls, and natural wood finishes are becoming standard features in modern homes. This connection to nature not only improves air quality but also enhances mental health and productivity."
        ]
      },
      {
        title: "Sustainable and Eco-Friendly Materials",
        content: [
          "Sustainability is no longer optional—it's essential. Homeowners are increasingly choosing recycled materials, reclaimed wood, and eco-friendly fabrics. Bamboo flooring, cork walls, and recycled glass countertops are gaining popularity.",
          "These materials not only reduce environmental impact but also add unique character and texture to spaces. The focus is on creating beautiful interiors that don't compromise the planet's future."
        ]
      },
      {
        title: "Warm Minimalism",
        content: [
          "Gone are the days of cold, stark minimalism. 2024 embraces 'warm minimalism'—a style that combines clean lines with cozy textures and warm color palettes. Think cream tones, natural wood, and soft textiles.",
          "This approach creates serene, uncluttered spaces that still feel inviting and lived-in. The key is to focus on quality over quantity, choosing fewer but more meaningful pieces."
        ]
      },
      {
        title: "Smart Home Integration",
        content: [
          "Technology seamlessly blends into design with smart lighting, automated climate control, and voice-activated systems. The goal is to make technology invisible while enhancing comfort and convenience.",
          "From smart mirrors in bathrooms to automated window treatments, these innovations are becoming standard features in modern homes, making daily life easier and more efficient."
        ]
      }
    ],
    conclusion: "These trends reflect a shift toward more conscious, comfortable, and connected living spaces. By incorporating these elements thoughtfully, you can create a home that's not only stylish but also sustainable and functional for years to come."
  },
  2: {
    intro: "In an age of information overload, minimalist web design has emerged as a powerful approach to creating clear, focused digital experiences. By stripping away unnecessary elements, minimalist design helps users focus on what truly matters.",
    sections: [
      {
        title: "The Power of White Space",
        content: [
          "White space, or negative space, is not empty space—it's a powerful design tool. It gives content room to breathe, improves readability, and creates visual hierarchy. Strategic use of white space can make your website feel more luxurious and professional.",
          "Don't be afraid of emptiness. Some of the most successful websites use generous amounts of white space to draw attention to key elements and create a sense of elegance and sophistication."
        ]
      },
      {
        title: "Typography as a Design Element",
        content: [
          "In minimalist design, typography becomes a primary visual element. With fewer decorative elements, your choice of fonts, sizes, and spacing becomes crucial. Bold, clean typography can make a powerful statement.",
          "Limit yourself to two or three complementary fonts maximum. Use font weight, size, and spacing to create hierarchy and guide users through your content naturally."
        ]
      },
      {
        title: "Strategic Use of Color",
        content: [
          "Minimalist design doesn't mean colorless design. A limited color palette can be incredibly impactful. Choose one or two accent colors to highlight important elements and create visual interest.",
          "Neutral backgrounds with strategic pops of color draw the eye exactly where you want it. This approach creates a sophisticated look while maintaining clarity and focus."
        ]
      },
      {
        title: "Functional Navigation",
        content: [
          "Navigation should be intuitive and unobtrusive. Minimalist websites often use simple, clean navigation menus that don't compete with content. Hamburger menus, sticky headers, and clear call-to-action buttons work well.",
          "Every navigation element should serve a purpose. Remove redundant links and focus on creating clear pathways to the most important content."
        ]
      }
    ],
    conclusion: "Minimalist web design is about intentionality—every element should have a purpose. By focusing on essential elements and removing distractions, you create websites that are not only beautiful but also highly effective at achieving their goals."
  },
  3: {
    intro: "Sustainable design is no longer a niche concept—it's becoming the standard. Creating eco-friendly living spaces means making conscious choices about materials, energy use, and long-term impact while maintaining style and comfort.",
    sections: [
      {
        title: "Choosing Sustainable Materials",
        content: [
          "The foundation of sustainable design lies in material selection. Opt for renewable resources like bamboo, cork, and reclaimed wood. These materials are not only environmentally friendly but also add unique character to your space.",
          "Look for certifications like FSC (Forest Stewardship Council) for wood products and GOTS (Global Organic Textile Standard) for fabrics. These ensure that materials are sourced responsibly and ethically."
        ]
      },
      {
        title: "Energy-Efficient Solutions",
        content: [
          "Sustainable living spaces prioritize energy efficiency. Install LED lighting, energy-efficient appliances, and smart thermostats to reduce energy consumption. Consider solar panels if feasible—they're more affordable than ever.",
          "Proper insulation and energy-efficient windows can dramatically reduce heating and cooling costs while improving comfort. These investments pay for themselves over time through reduced utility bills."
        ]
      },
      {
        title: "Indoor Air Quality",
        content: [
          "Sustainable design considers health as well as environment. Choose low-VOC paints, natural cleaning products, and materials that don't off-gas harmful chemicals. Indoor plants not only look beautiful but also purify the air naturally.",
          "Proper ventilation is crucial. Consider installing air purifiers or heat recovery ventilation systems to maintain fresh, clean air throughout your home."
        ]
      },
      {
        title: "Water Conservation",
        content: [
          "Water-saving fixtures like low-flow toilets, faucets, and showerheads can reduce water consumption by up to 50% without sacrificing performance. Rainwater harvesting systems can be used for irrigation and other non-potable uses.",
          "In landscaping, choose native plants that require less water and maintenance. Drought-resistant gardens can be just as beautiful as traditional lawns while using a fraction of the water."
        ]
      }
    ],
    conclusion: "Creating sustainable living spaces is an investment in both your future and the planet's. These choices not only reduce environmental impact but often result in healthier, more comfortable homes with lower operating costs. Sustainability and style can—and should—go hand in hand."
  },
  4: {
    intro: "Color is one of the most powerful tools in a designer's arsenal. Understanding color psychology—how different hues affect emotions, behavior, and perception—can transform ordinary spaces into extraordinary experiences.",
    sections: [
      {
        title: "The Emotional Impact of Color",
        content: [
          "Colors evoke specific emotional responses. Blue promotes calmness and trust, making it ideal for bedrooms and offices. Red energizes and stimulates appetite, perfect for dining areas. Green connects us to nature and promotes balance and harmony.",
          "Yellow brings optimism and creativity, while purple conveys luxury and sophistication. Understanding these associations helps you create spaces that support their intended purpose and evoke desired feelings."
        ]
      },
      {
        title: "Cultural Considerations",
        content: [
          "Color meanings vary across cultures. While white symbolizes purity in Western cultures, it represents mourning in some Eastern cultures. Red signifies luck and prosperity in China but can indicate danger in Western contexts.",
          "When designing for diverse audiences, research cultural color associations to ensure your choices resonate positively with your target users. This is especially important for global brands and international projects."
        ]
      },
      {
        title: "Color in Digital Design",
        content: [
          "In web and app design, color affects usability and conversion rates. High-contrast color combinations improve readability and accessibility. Strategic use of color can guide users' attention and encourage specific actions.",
          "Call-to-action buttons in contrasting colors can increase click-through rates significantly. Consistent color coding helps users navigate interfaces intuitively and builds brand recognition."
        ]
      },
      {
        title: "Creating Effective Color Palettes",
        content: [
          "Start with a dominant color that reflects your brand or space's personality. Add complementary colors for contrast and analogous colors for harmony. Use the 60-30-10 rule: 60% dominant color, 30% secondary color, 10% accent color.",
          "Test your color palette in different lighting conditions and on various devices. Colors can appear dramatically different depending on context, so always verify your choices in real-world conditions."
        ]
      }
    ],
    conclusion: "Color psychology is both an art and a science. By understanding how colors affect human psychology and behavior, you can create designs that not only look beautiful but also function effectively, supporting user goals and creating memorable experiences."
  }
};

export default function BlogDetailPage() {
  const location = useLocation();
  const [post, setPost] = useState<any>(null);
  const [content, setContent] = useState<any>(null);
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const id = params.get('id');
    
    if (id) {
      const foundPost = blogPosts.find(p => p.id === parseInt(id));
      setPost(foundPost);
      setContent(blogContents[parseInt(id)]);
    }

    window.scrollTo(0, 0);
  }, [location]);

  // SEO Configuration for blog posts
  useSEO({
    title: post ? `${post.title} - Urban Spaces Blog` : 'Blog - Urban Spaces',
    description: post ? post.excerpt : 'Read the latest insights on interior design, web development, and digital solutions from Urban Spaces.',
    keywords: post ? `${post.category}, interior design, web design, ${post.title.toLowerCase()}` : 'blog, interior design, web development',
    canonical: post ? `${siteUrl}/blog-detail?id=${post.id}` : `${siteUrl}/blog-detail`,
    ogType: 'article',
    schema: post ? {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: post.title,
      description: post.excerpt,
      image: post.image,
      datePublished: post.date,
      dateModified: post.date,
      author: {
        '@type': 'Person',
        name: post.author.name,
        jobTitle: post.author.role
      },
      publisher: {
        '@type': 'Organization',
        name: 'Urban Spaces',
        logo: {
          '@type': 'ImageObject',
          url: `${siteUrl}/logo.png`
        }
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `${siteUrl}/blog-detail?id=${post.id}`
      },
      articleSection: post.category,
      wordCount: content ? content.intro.length + content.sections.reduce((acc: number, s: any) => acc + s.content.join(' ').length, 0) : 0
    } : undefined
  });

  if (!post || !content) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-4xl mx-auto px-6 py-24">
          <p className="text-center text-gray-600">Article not found</p>
          <div className="text-center mt-6">
            <Link to="/" className="text-teal-600 hover:text-teal-700 font-medium cursor-pointer">
              ← Back to Home
            </Link>
          </div>
        </div>
        <BlogFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <article className="pt-32 pb-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <Link to="/" className="inline-flex items-center space-x-2 text-teal-600 hover:text-teal-700 font-medium text-sm mb-8 cursor-pointer whitespace-nowrap">
            <i className="ri-arrow-left-line"></i>
            <span>Back to Home</span>
          </Link>

          <div className="mb-8">
            <div className="flex items-center space-x-4 text-sm text-gray-600 mb-4">
              <span className="px-3 py-1 bg-teal-50 text-teal-700 rounded-full font-medium">
                {post.category}
              </span>
              <span>{post.date}</span>
              <span>•</span>
              <span>{post.readTime}</span>
            </div>
            
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              {post.title}
            </h1>

            <div className="flex items-center space-x-4">
              <img
                src={post.author.avatar}
                alt={post.author.name}
                className="w-12 h-12 rounded-full object-cover"
              />
              <div>
                <p className="font-medium text-gray-900">{post.author.name}</p>
                <p className="text-sm text-gray-600">{post.author.role}</p>
              </div>
            </div>
          </div>

          <div className="relative rounded-2xl overflow-hidden mb-12 h-96">
            <img
              src={post.image}
              alt={post.title}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 leading-relaxed mb-8">
              {content.intro}
            </p>

            {content.sections.map((section: any, index: number) => (
              <div key={index}>
                <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {section.title}
                </h2>
                {section.content.map((paragraph: string, pIndex: number) => (
                  <p key={pIndex} className="text-gray-700 leading-relaxed mb-6">
                    {paragraph}
                  </p>
                ))}
              </div>
            ))}

            <div className="bg-gray-50 border-l-4 border-teal-500 p-6 my-8 rounded-r-lg">
              <p className="text-gray-800 italic">
                "{post.excerpt}"
              </p>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12" style={{ fontFamily: 'Playfair Display, serif' }}>
              Final Thoughts
            </h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              {content.conclusion}
            </p>
          </div>

          <div className="mt-16 pt-8 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-600">Share this article:</span>
                <div className="flex items-center space-x-3">
                  <a href="#" className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-teal-500 hover:text-white transition-colors cursor-pointer">
                    <i className="ri-twitter-line"></i>
                  </a>
                  <a href="#" className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-teal-500 hover:text-white transition-colors cursor-pointer">
                    <i className="ri-facebook-line"></i>
                  </a>
                  <a href="#" className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-teal-500 hover:text-white transition-colors cursor-pointer">
                    <i className="ri-linkedin-line"></i>
                  </a>
                  <a href="#" className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-teal-500 hover:text-white transition-colors cursor-pointer">
                    <i className="ri-pinterest-line"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-12 mt-24">
          <div className="bg-teal-600 rounded-3xl p-12 text-center">
            <h3 className="text-3xl lg:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Ready to Start Your Project?
            </h3>
            <p className="text-white/90 text-base mb-8 max-w-2xl mx-auto">
              Let's bring your vision to life. Schedule a consultation with our expert team today.
            </p>
            <Link
              to="/?section=booking"
              onClick={() => {
                setTimeout(() => {
                  const bookingSection = document.getElementById('booking');
                  if (bookingSection) {
                    bookingSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }, 100);
              }}
              className="inline-block bg-white text-teal-600 px-8 py-4 rounded-lg font-medium hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              Book a Consultation
            </Link>
          </div>
        </div>
      </article>

      <BlogFooter />
      <WhatsAppChat />
    </div>
  );
}
